import React, { useRef, useEffect, useState, useCallback } from 'react'
import { apiUploadFile, getErrorMessage } from '../../utils/api'

export interface CopilotAttachmentDraft {
  id: string
  url: string
  type: 'image' | 'video' | 'file'
  name: string
  size: number
}

interface CopilotComposerProps {
  onSend: (text: string, attachments?: CopilotAttachmentDraft[]) => void
  /** Disable input + send while a response is streaming. */
  disabled?: boolean
  prefill?: string | null
  prefillNonce?: number
}

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ALLOWED = '.jpg,.jpeg,.png,.gif,.webp,.bmp,.tiff,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt'

const SendIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
  </svg>
)
const ClipIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
  </svg>
)

function isImage(a: CopilotAttachmentDraft): boolean {
  return a.type === 'image' || /\.(png|jpe?g|gif|webp|bmp|tiff?)$/i.test(a.name || '')
}

/**
 * Multiline Copilot composer. Enter sends; Shift+Enter inserts a newline. Now
 * supports pasting/uploading/dropping images + files, which are sent to the
 * Copilot so it can look at them (screenshots etc.).
 */
export const CopilotComposer: React.FC<CopilotComposerProps> = ({
  onSend,
  disabled,
  prefill,
  prefillNonce,
}) => {
  const [value, setValue] = useState('')
  const [attachments, setAttachments] = useState<CopilotAttachmentDraft[]>([])
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const ref = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`
  }, [value])

  useEffect(() => {
    if (!prefill) return
    setValue(prefill)
    const el = ref.current
    if (el) {
      el.focus()
      const len = prefill.length
      requestAnimationFrame(() => {
        try { el.setSelectionRange(len, len) } catch { /* ignore */ }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill, prefillNonce])

  const uploadFiles = useCallback(async (files: File[]) => {
    if (!files.length) return
    setUploadError(null)
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        setUploadError(`"${file.name || 'file'}" exceeds the 10MB limit.`)
        continue
      }
      setUploading(true)
      try {
        const result = await apiUploadFile<CopilotAttachmentDraft>('/agent/upload', file)
        setAttachments((prev) => [...prev, result])
      } catch (err) {
        setUploadError(getErrorMessage(err, 'Upload failed'))
      } finally {
        setUploading(false)
      }
    }
  }, [])

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    await uploadFiles(Array.from(files))
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items
      if (!items) return
      const images: File[] = []
      for (let i = 0; i < items.length; i++) {
        const it = items[i]
        if (it.kind === 'file' && it.type.startsWith('image/')) {
          const blob = it.getAsFile()
          if (!blob) continue
          const ext = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg')
          const name = blob.name && /\.[a-z0-9]+$/i.test(blob.name) ? blob.name : `pasted-${Date.now()}.${ext}`
          images.push(new File([blob], name, { type: blob.type }))
        }
      }
      if (images.length > 0) {
        e.preventDefault()
        void uploadFiles(images)
      }
    },
    [uploadFiles]
  )

  const removeAttachment = (id: string) =>
    setAttachments((prev) => prev.filter((a) => a.id !== id))

  const handleDragOver = (e: React.DragEvent) => {
    if (Array.from(e.dataTransfer?.types || []).includes('Files')) {
      e.preventDefault()
      if (!disabled) setIsDragging(true)
    }
  }
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    if (e.currentTarget === e.target) setIsDragging(false)
  }
  const handleDrop = (e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return
    e.preventDefault()
    setIsDragging(false)
    if (disabled) return
    const files = Array.from(e.dataTransfer?.files || [])
    if (files.length) void uploadFiles(files)
  }

  const canSend = (value.trim().length > 0 || attachments.length > 0) && !disabled && !uploading

  const submit = () => {
    if (!canSend) return
    onSend(value.trim(), attachments.length > 0 ? attachments : undefined)
    setValue('')
    setAttachments([])
    setUploadError(null)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`relative rounded-2xl border bg-surface-elevated shadow-sm transition-colors px-3 py-2 ${
        isDragging ? 'border-brand-500 ring-2 ring-brand-500/30' : 'border-border focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500'
      }`}
    >
      {isDragging && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-brand-50/80 dark:bg-brand-900/30 text-sm font-medium text-brand-700 dark:text-brand-200 pointer-events-none">
          Drop files to attach
        </div>
      )}

      {/* Attachment previews */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {attachments.map((a) => (
            <div key={a.id} className="relative group">
              {isImage(a) ? (
                <img src={a.url} alt={a.name} className="w-16 h-16 rounded-lg object-cover border border-border" />
              ) : (
                <div className="w-16 h-16 rounded-lg border border-border bg-surface-secondary flex flex-col items-center justify-center px-1 text-center">
                  <span className="text-[9px] font-semibold uppercase text-txt-tertiary">FILE</span>
                  <span className="text-[9px] text-txt-secondary truncate w-full">{a.name}</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => removeAttachment(a.id)}
                aria-label={`Remove ${a.name}`}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-800 text-white flex items-center justify-center text-xs shadow hover:bg-black"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {uploadError && <p className="mb-1.5 text-xs text-error">{uploadError}</p>}

      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED}
          onChange={handleFileSelect}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading}
          aria-label="Attach a file or image"
          title="Attach a file or image"
          className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl text-txt-tertiary hover:text-brand-600 hover:bg-surface-hover disabled:opacity-40 transition-colors"
        >
          <ClipIcon />
        </button>
        <textarea
          ref={ref}
          rows={1}
          value={value}
          disabled={disabled}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={handlePaste}
          placeholder={disabled ? 'Waiting for response…' : (uploading ? 'Uploading…' : 'Ask the Copilot anything, or paste an image…')}
          aria-label="Message the Copilot"
          className="flex-1 resize-none bg-transparent text-sm text-txt-primary placeholder:text-txt-tertiary focus:outline-none disabled:opacity-60 leading-relaxed py-1.5 max-h-[200px]"
        />
        <button
          type="button"
          onClick={submit}
          disabled={!canSend}
          aria-label="Send message"
          title="Send (Enter)"
          className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <SendIcon />
        </button>
      </div>
    </div>
  )
}
